<template>
  <div id="contract-action-buttons">
    <template v-if="(contract.status === STATUSES.DISBURSED) || (contract.status === STATUSES.OVERDUE)">
      <primary-button @click="renew()" blue :text="$t('RENEW')" />
      <primary-button @click="repay()" class="yellow" :text="$t('REDEEM')" />
      <primary-button v-if="contract.status === STATUSES.OVERDUE" @click="liquidize()" class="red" :text="$t('LIQUIDIZE')" />
      <primary-button @click="togglePartialRepayDialog()" class="green" :text="$t('PARTIAL_REPAY')" />
      <primary-button @click="toggleWaiveInterestDialog()" class="green" :text="$t('WAIVE_INTEREST')" />
      <br />
      <primary-button @click="undoDisbursal()" :text="$t('UNDO_DISBURSEMENT')" v-if="(superuser || moment(contract.submittedOn).add(3, 'day').format(DATE_FORMAT) > today) && canUndoContract" />
      <partial-repay-dialog v-model="partialRepayOn" :disbursed-on="contract.submittedOn" @repay="partialRepay" />
      <waive-interest-dialog v-model="waiveInterestOn" :disbursed-on="contract.submittedOn" :interest-amount="contract.interestAmount" @waive="waiveInterest" />
    </template>
    <template v-else-if="contract.status === STATUSES.APPROVED">
      <primary-button @click="toggleDialog()" blue :text="$t('DISBURSE')" v-if="(contract.submittedOn <= today)" />
      <primary-button @click="undoApproval()" :text="$t('UNDO_APPROVAL')" v-if="(superuser || moment(contract.submittedOn).add(3, 'day').format(DATE_FORMAT) > today) && canUndoContract" />
      <disburse-dialog v-model="on" @disburse="disburse" />
    </template>
    <template v-else-if="contract.status === STATUSES.PENDING">
      <primary-button @click="approve()" blue :text="$t('APPROVE')" />
      <primary-button class="yellow" @click="edit()" :text="$t('EDIT')" v-if="!contract.parentId && (moment(contract.submittedOn).add(3, 'day').format(DATE_FORMAT) > today)" />
      <primary-button @click="cancel()" :text="$t(contract.parentId ? 'UNDO_RENEWAL' : 'REJECT')" v-if="(contract.parentId && (superuser || (moment(contract.submittedOn).add(3, 'day').format(DATE_FORMAT) > today))) || !contract.parentId" />
      <cancel-dialog v-model="on" @reject="reject" />
    </template>
    <template v-else-if="contract.status === STATUSES.REPAID && canUndoContract">
      <primary-button @click="undoRepayment()" :text="$t('UNDO_REPAYMENT')" v-if="superuser || moment(contract.repaidOn).add(3, 'day').format(DATE_FORMAT) > today" />
    </template>
    <template v-else-if="contract.status === STATUSES.RENEWED && canUndoContract">
      <primary-button @click="undoRenewal()" :text="$t('UNDO_RENEWAL')" v-if="superuser || moment(contract.renewedOn).add(3, 'day').format(DATE_FORMAT) > today"/>
    </template>

    <template v-if="contract.parentId">
      <primary-button blue @click="sendRenewalNotification()" :text="$t('SEND_ZALO_NOTIFICATION')" v-if="superuser"/>
    </template>

    <confirm-modal />
  </div>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import moment from 'moment';

import { CONTRACT_STATUSES as STATUSES, TRANSACTION_ACTIONS, PATHS, DATE_FORMAT, PERMISSIONS } from '@/consts';
import { applyParams, addCommas } from '@/helpers';
import{ hasPermissions } from '@/helpers/permission';

import CancelDialog from '@/components/CancelContractDialog';
import ConfirmModal from '@/components/ConfirmModal';
import PrimaryButton from '@/components/PrimaryButton';

import DisburseDialog from './DisburseDialog';
import PartialRepayDialog from './PartialRepayDialog';
import WaiveInterestDialog from './WaiveInterestDialog';

import ContractDetailService from '../ContractDetailServices';

const { useState, useActions } = createNamespacedHelpers('contractDetail');
const { useActions: useConfirmActions } = createNamespacedHelpers('confirm');
const { useActions: useModalActions } = createNamespacedHelpers('modal');
const { useActions: useFlashActions } = createNamespacedHelpers('flash');
const { useGetters: useUserGetters } = createNamespacedHelpers('user');

const today = moment().format(DATE_FORMAT);

export default {
  name: 'ActionButton',
  components: {
    PrimaryButton,
    CancelDialog,
    DisburseDialog,
    ConfirmModal,
    PartialRepayDialog,
    WaiveInterestDialog
  },
  setup(props, { root }) {
    const {
      getContractDetail,
      approveContract,
      rejectContract,
      undoContractApproval,
      disburseContract,
      undoContractDisbursal,
      undoContractRepayment,
      undoContractRenewal,
      undoParentContractRenewal
    } = useActions([
      'getContractDetail',
      'approveContract',
      'rejectContract',
      'undoContractApproval',
      'disburseContract',
      'undoContractDisbursal',
      'undoContractRepayment',
      'undoContractRenewal',
      'undoParentContractRenewal'
    ]);

    const { targetingContract: contract } = useState(['targetingContract']);
    const { setTitleModal, setOnConfirm } = useConfirmActions(['setTitleModal', 'setOnConfirm']);
    const { toggleConfirmModal: toggleConfirm } = useModalActions(['toggleConfirmModal']);
    const { info, error } = useFlashActions(['info', 'error']);
    const { superuser } = useUserGetters(['superuser']);

    const repay = () => {
      root.$router.push({ name: 'contract-repayment', params: { id: contract.value.finfluxId } });
    };

    const liquidize = () => {
      root.$router.push({ name: 'contract-liquidization', params: { id: contract.value.finfluxId } });
    };

    const renew = () => {
      root.$router.push({ name: 'contract-renewal', params: { id: contract.value.finfluxId } });
    };

    const on = ref(false);
    const partialRepayOn = ref(false);
    const waiveInterestOn = ref(false);

    const approve = () => {
      approveContract(contract.value.finfluxId);
    };

    const undoApproval = () => {
      toggleConfirm(true);
      setTitleModal(root.$t('ARE_YOU_SURE'));
      setOnConfirm(() => {
        undoContractApproval(contract.value.finfluxId);
      });
    };

    const reject = ({ reason }) => {
      toggleConfirm(true);
      setTitleModal(root.$t('ARE_YOU_SURE_THIS_ACTION_IS_INREVERSIBLE'));
      setOnConfirm(() => {
        rejectContract({
          id: contract.value.finfluxId,
          data: { reason }
        });
      });
    };

    const disburse = ({ type }) => {
      disburseContract({
        id: contract.value.finfluxId,
        data: { type }
      });
    };

    const toggleDialog = () => {
      if (
        contract.value.parent
        && contract.value.parent.approvedAmount
        && (contract.value.parent.approvedAmount >= contract.value.approvedAmount)
      ) {
        disburse({});
        return;
      }

      on.value = !on.value;
    };

    const partialRepay = async ({ registeredOn, type, amount }) => {
      try {
        if (contract.value.maturesOn < registeredOn) {
          await ContractDetailService.chargeOverdueFee(contract.value.id, {
            registeredOn,
            amount: -1
          });
        }

        await ContractDetailService.addTransaction(contract.value.id, {
          registeredOn,
          type,
          amount,
          action: TRANSACTION_ACTIONS.REPAY,
          actionOn: registeredOn
        });

        info(root.$t('PARTIAL_REPAID_SUCCESSFULLY'));
        ContractDetailService.addContractNotes(contract.value.id, `Thanh toán một phần (${addCommas(amount)})`);

        getContractDetail(contract.value.finfluxId);

        partialRepayOn.value = false;
      } catch (e) {
        // TODO: prompt correct detailed error message based on error code
        const { message } = e;
        error(message);
      }
    };

    const waiveInterest = async ({ registeredOn, amount }) => {
      try {
        await ContractDetailService.addTransaction(contract.value.id, {
          registeredOn,
          amount,
          action: TRANSACTION_ACTIONS.WAIVE,
          actionOn: registeredOn
        });

        info(root.$t('WAIVED_INTEREST_SUCCESSFULLY'));
        ContractDetailService.addContractNotes(contract.value.id, `Giảm lãi (${addCommas(amount)})`);

        getContractDetail(contract.value.finfluxId);

        waiveInterestOn.value = false;
      } catch (e) {
        // TODO: prompt correct detailed error message based on error code
        const { message } = e;
        error(message);
      }
    };

    const togglePartialRepayDialog = () => {
      partialRepayOn.value = !partialRepayOn.value;
    };

    const toggleWaiveInterestDialog = () => {
      waiveInterestOn.value = !waiveInterestOn.value;
    };

    const edit = () => {
      root.$router.push(applyParams(PATHS.CONTRACT_EDITOR, contract.value.finfluxId));
    };

    const undoDisbursal = () => {
      toggleConfirm(true);
      setTitleModal(root.$t('ARE_YOU_SURE'));
      setOnConfirm(() => {
        undoContractDisbursal(contract.value.finfluxId);
      });
    };

    const undoRepayment = () => {
      toggleConfirm(true);
      setTitleModal(root.$t('ARE_YOU_SURE'));
      setOnConfirm(() => {
        undoContractRepayment(contract.value.finfluxId);
      });
    };

    const undoRenewal = (parent) => {
      toggleConfirm(true);

      if (parent) {
        setTitleModal(root.$t('ARE_YOU_SURE_CONTRACT_ID_WILL_BE_PERMANENTLY_REJECTED', { contractId: contract.value.finfluxId }));
      } else if (contract.value.renewal) {
        setTitleModal(root.$t('ARE_YOU_SURE_CONTRACT_ID_WILL_BE_PERMANENTLY_REJECTED', { contractId: contract.value.renewal.finfluxId }));
      }

      setOnConfirm(() => {
        if (parent) {
          undoParentContractRenewal(contract.value.parentId);
        } else {
          undoContractRenewal(contract.value.finfluxId);
        }
      });
    };

    const cancel = () => {
      if (contract.value.parentId) {
        undoRenewal(true);
      } else {
        toggleDialog();
      }
    };

    const sendRenewalNotification = () => {
      ContractDetailService.sendRenewedNotification(contract.value.id);
    };

    const canUndoContract = computed(() => hasPermissions([PERMISSIONS.UNDO_CONTRACT]));

    return {
      renew,
      repay,
      liquidize,
      reject,
      on,
      approve,
      cancel,
      toggleDialog,
      STATUSES,
      contract,
      undoApproval,
      edit,
      disburse,
      undoDisbursal,
      today,
      undoRepayment,
      undoRenewal,
      moment,
      DATE_FORMAT,
      partialRepayOn,
      partialRepay,
      togglePartialRepayDialog,
      toggleWaiveInterestDialog,
      waiveInterestOn,
      waiveInterest,
      superuser,
      canUndoContract,
      sendRenewalNotification
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/colors";

#contract-action-buttons {
  text-align: center;

  .primary-button {
    margin-left: 10px;
    margin-bottom: 7px;
    border-radius: 4px;
    padding-left: 20px;
    padding-right: 20px;

    &.yellow {
      background-color: $yellowCrayola;
    }

    &.green {
      background-color: $shamrockGreen;
    }
  }
}
</style>
